import { NgClass, NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, TemplateRef, computed, input } from '@angular/core';
import { uniqueId } from '@co-3/ctrl-ui/common';

/**
 * @description Expandable content container with headline.
 *  Use this component for an entry in a faq list.
 * @argument {TemplateRef<any>|string} headline Headline content. Can be either a string or TemplateRef.
 * @example usage with ng-template (use when html-entities or inline elements need to be rendered):
 * ```html
 *  <ng-template #faqHeadline>This is the headline</ng-template>
 *  <app-faq [headline]="faqHeadline">
 *      This is the content
 *  </app-faq>
 * ```
 * @example usage with string input
 * ```html
 *  <app-faq headline="This is the headline">
 *      This is the content
 *  </app-faq>
 * ```
 */
@Component({
    selector: 'app-faq',
    templateUrl: './faq.component.html',
    styleUrl: './faq.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [NgClass, NgTemplateOutlet],
})
export class FaqComponent {
    public headline = input.required<string | TemplateRef<unknown>>();

    protected headlineAsTemplate = computed((): TemplateRef<unknown> | undefined => {
        const headline = this.headline();
        return headline instanceof TemplateRef ? headline : undefined;
    });

    protected open: boolean = false;

    private uniqueId: string = uniqueId();
    protected buttonId: string = `faq-button-${this.uniqueId}`;
    protected sectionId: string = `faq-section-${this.uniqueId}`;
}
